import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./CourseDetailProgram.css";
import { Check, Download } from "lucide-react";
import trainerAvatar from "../../../../assets/images/academy/s1.webp";
import sadIcon from "../../../../assets/icons/sad.svg";
import { useTranslation } from "react-i18next";
import { getCourseTrainings } from "../../../../api/trainings";
import { resolveMediaUrl } from "../../../../utils/media";
import { scrollToElementById } from "../../../../utils/scrollToId";

const CourseDetailProgram = ({ course }) => {
  const { i18n } = useTranslation();
  const [trainings, setTrainings] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    let isMounted = true;
    const loadTrainings = async () => {
      const courseId = Number(course?.id);
      if (!Number.isFinite(courseId) || courseId <= 0) {
        setTrainings([]);
        return;
      }
      setLoading(true);
      try {
        const data = await getCourseTrainings({
          courseId,
          language: String(i18n.language || "en").toLowerCase(),
        });
        if (!isMounted) return;
        setTrainings(Array.isArray(data) ? data : []);
      } catch {
        if (isMounted) setTrainings([]);
      } finally {
        if (isMounted) setLoading(false);
      }
    };
    loadTrainings();
    return () => {
      isMounted = false;
    };
  }, [course?.id, i18n.language]);

  const selectedTraining = useMemo(() => {
    if (!Array.isArray(trainings) || !trainings.length) return null;
    return [...trainings]
      .sort(
        (a, b) =>
          Number(a?.displayOrder || 0) - Number(b?.displayOrder || 0) ||
          Number(a?.id || 0) - Number(b?.id || 0)
      )[0];
  }, [trainings]);

  if (loading) return null;

  if (!selectedTraining) {
    return (
      <section className="courseProgram">
        <div className="courseProgram-title">
          Our training <span>program</span>
        </div>
        <div className="courseProgram-empty" aria-label="No trainings available">
          <img src={sadIcon} alt="" aria-hidden className="courseProgram-emptyIcon" />
          <p className="courseProgram-emptyTitle">There are no trainings at the moment.</p>
          <p className="courseProgram-emptyText">
            Please check back soon for upcoming sessions.
          </p>
        </div>
      </section>
    );
  }

  const gainItems = Array.isArray(selectedTraining?.benefits)
    ? selectedTraining.benefits
    : [];
  const trainerHighlights = Array.isArray(selectedTraining?.trainerHighlights)
    ? selectedTraining.trainerHighlights
    : [];
  const trainerHighlightsText = trainerHighlights
    .map((item) => String(item?.text || "").trim())
    .filter(Boolean)
    .join("  •  ");
  const trainerImageSrc =
    resolveMediaUrl(selectedTraining?.trainerAvatarImageUrl) || trainerAvatar;

  return (
    <section className="courseProgram">
      <div className="courseProgram-title">
        Our training <span>program</span>
      </div>

      <div className="courseProgram-grid">
        <div className="courseProgram-main">
          <div className="courseProgram-mainHeader">
            <h3>
              {selectedTraining?.title ||
                course?.title ||
                "Study course program title example"}
            </h3>
            <span className="courseProgram-minus">-</span>
          </div>

          <p className="courseProgram-desc">
            {selectedTraining?.description ||
              course?.description ||
              "Course description text will be available soon."}
          </p>

          <ul className="courseProgram-gainList">
            {gainItems.map((item, idx) => (
              <li key={item?.id || idx}>
                <Check size={16} />
                <span>{item?.text}</span>
              </li>
            ))}
          </ul>

          <div className="courseProgram-actions">
            <Link to="/contactus#contact-form" className="courseProgram-register">
              {selectedTraining?.registerNowText || "Register now"}
            </Link>
            <Link to="/downloads" className="courseProgram-brochure">
              {selectedTraining?.downloadBrochureText || "Download brochure"}{" "}
              <Download size={15} />
            </Link>
            {/* <button
              type="button"
              className="courseProgram-questionBtn"
              onClick={() => scrollToElementById("contact-form")}
            >
              If you have any questions?
            </button> */}
          </div>

          <div className="courseProgram-trainer">
            <h4>{selectedTraining?.trainerSectionTitle || "Meet the trainer"}</h4>
            <div className="courseProgram-trainerBody">
              <img
                src={trainerImageSrc}
                alt={selectedTraining?.trainerName || "Trainer"}
                onError={(e) => {
                  if (e.currentTarget.src !== trainerAvatar) {
                    e.currentTarget.src = trainerAvatar;
                  }
                }}
              />
              <div>
                <h5>{selectedTraining?.trainerName || "Trainer"}</h5>
                <p>{selectedTraining?.trainerBio || "Trainer information coming soon."}</p>
                {trainerHighlightsText ? <small>{trainerHighlightsText}</small> : null}
              </div>
            </div>
          </div>
        </div>

        <aside className="courseProgram-info">
          <h3>{selectedTraining?.courseInfoTitle || "Course information"}</h3>
          <p>{selectedTraining?.courseInfoSubtitle || "Details shown once schedule is confirmed"}</p>
          <dl>
            <div><dt>Date</dt><dd>{selectedTraining?.dateText || "-"}</dd></div>
            <div><dt>Location</dt><dd>{selectedTraining?.locationText || "-"}</dd></div>
            <div><dt>Format</dt><dd>{selectedTraining?.formatText || "-"}</dd></div>
            <div><dt>Language</dt><dd>{selectedTraining?.languageText || "-"}</dd></div>
            <div><dt>Seats remaining</dt><dd>{selectedTraining?.seatsRemainingText || "-"}</dd></div>
            <div><dt>Contact</dt><dd>{selectedTraining?.contactText || "-"}</dd></div>
          </dl>
        </aside>
      </div>
    </section>
  );
};

export default CourseDetailProgram;
